import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Search, Smartphone } from 'lucide-react';
import { useMobile } from './MobileContext';
import mapi from './api';
import { MobileShell, MobileHeader, C } from './MobileShell';
import { startImpersonation, isImpersonating } from '@/lib/impersonation';

const AREA_LABEL = { lehrer: 'Lehrer', schueler: 'Schüler', eltern: 'Eltern' };

/**
 * s-admin: App-Konten suchen und deren Companion per Kontowechsel öffnen.
 */
export default function MobileAppAccounts() {
  const { me, loading, area } = useMobile();
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState('');
  const [openingId, setOpeningId] = useState(null);
  const [loadingList, setLoadingList] = useState(true);

  const canUse = Boolean(me?.is_super_admin)
    && area === 'verwaltung'
    && !isImpersonating();

  const load = useCallback(async () => {
    if (!canUse) return;
    setLoadingList(true);
    try {
      const r = await mapi.get('/admin/app-verwaltung/accounts', {
        params: { search: search || undefined },
      });
      setAccounts(r.data || []);
    } catch {
      toast.error('App-Konten konnten nicht geladen werden');
      setAccounts([]);
    } finally {
      setLoadingList(false);
    }
  }, [canUse, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  if (loading) return null;
  if (!me) return <Navigate to="/m/login" replace />;
  if (!me.is_super_admin || area !== 'verwaltung') {
    return <Navigate to="/m" replace />;
  }
  if (isImpersonating()) {
    return <Navigate to="/m" replace />;
  }

  const open = async (a) => {
    if (!a?.is_active || !a.user_id) return;
    setOpeningId(a.user_id);
    try {
      await startImpersonation(a.user_id, { to: '/m' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Companion-Öffnen fehlgeschlagen');
      setOpeningId(null);
    }
  };

  return (
    <MobileShell>
      <MobileHeader title="Companion öffnen" backTo="/m" onRefresh={load} />
      <div className="px-4 pt-3 pb-2">
        <p className="text-xs mb-3" style={{ color: C.dim }} data-testid="mobile-app-accounts-hint">
          Kontowechsel in ein App-Konto (Lehrer, Schüler oder Eltern). Banner „Zurück“ beendet den Wechsel.
        </p>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-3" style={{ color: C.dim }} />
          <input
            className="w-full pl-9 pr-3 py-2.5 rounded-xl text-sm outline-none"
            style={{ background: C.card, border: `1px solid ${C.border}`, color: C.text }}
            placeholder="Name oder E-Mail…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="mobile-app-accounts-search"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto" data-testid="mobile-app-accounts-list">
        {loadingList && (
          <p className="px-4 py-8 text-center text-sm" style={{ color: C.dim }}>Laden…</p>
        )}
        {!loadingList && !accounts.length && (
          <p className="px-4 py-8 text-center text-sm" style={{ color: C.dim }}>Keine App-Konten gefunden</p>
        )}
        {!loadingList && accounts.map((a) => {
          const areas = (a.identity_areas || []).map((x) => AREA_LABEL[x] || x).join(' · ');
          const sub = [a.email, areas || (a.identity_names || []).join(', ')].filter(Boolean).join(' · ');
          return (
            <button
              key={a.user_id}
              type="button"
              disabled={!a.is_active || openingId === a.user_id}
              onClick={() => open(a)}
              data-testid={`mobile-app-account-open-${a.user_id}`}
              className="w-full flex items-center gap-3 px-4 py-3.5 text-left active:opacity-70 disabled:opacity-40"
              style={{ background: C.card, borderBottom: `1px solid ${C.border}`, color: C.text }}
            >
              <Smartphone className="w-5 h-5 shrink-0" style={{ color: C.red }} />
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium truncate">{a.full_name || a.email || '—'}</span>
                {sub && <span className="block text-xs truncate" style={{ color: C.dim }}>{sub}</span>}
              </span>
              <span className="text-xs font-semibold shrink-0" style={{ color: C.red }}>
                {openingId === a.user_id ? '…' : 'Öffnen'}
              </span>
            </button>
          );
        })}
      </div>
    </MobileShell>
  );
}
